import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';

const MAX_LENGTH = 500;

// Fast enough to feel like a conversation, slow enough not to hammer a forum
// that may be on shared hosting.
const POLL_ACTIVE = 3000;
const POLL_HIDDEN = 20000;

// Below this distance from the bottom, new messages scroll into view; above
// it the reader is looking at history and is left alone.
const STICK_THRESHOLD = 80;

/**
 * The waiting room's chat.
 *
 * Polls for messages newer than the highest id it holds, so each request
 * carries only what is new. Sending is optimistic: the message appears
 * immediately and is reconciled when the server answers.
 */
export default class LobbyChat extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    this.messages = [];
    this.loading = true;
    this.open = false;
    this.canPost = false;

    this.draft = '';
    this.sending = false;
    this.error = null;

    this.timer = null;
    this.failures = 0;
    this.pinToEnd = false;
    this.wasAtEnd = true;

    this.onVisibility = () => {
      if (!document.hidden) this.poll(true);
    };

    document.addEventListener('visibilitychange', this.onVisibility);

    this.poll(true);
  }

  onremove(vnode) {
    super.onremove(vnode);

    this.destroyed = true;
    if (this.timer) clearTimeout(this.timer);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  oncreate(vnode) {
    super.oncreate(vnode);

    this.list = vnode.dom.querySelector('.HappyQuizChat-list');
    this.scrollToEnd();
  }

  /**
   * Records where the reader was before Mithril patches the list, because
   * afterwards the list is taller and the old position no longer reads as
   * "at the end".
   */
  onbeforeupdate() {
    this.wasAtEnd = this.atEnd();
  }

  onupdate(vnode) {
    super.onupdate(vnode);

    if (!this.list) this.list = vnode.dom.querySelector('.HappyQuizChat-list');

    // Follow the conversation only for a reader who was already at the end.
    // Someone who scrolled up to read history is left where they are.
    if (this.pinToEnd || this.wasAtEnd) {
      this.pinToEnd = false;
      this.scrollToEnd();
    }
  }

  contestId() {
    return this.attrs.contestId;
  }

  highestId() {
    // Optimistic entries carry no server id, so they cannot become the cursor.
    return this.messages.reduce((max, message) => (message.id > max ? message.id : max), 0);
  }

  poll(immediate = false) {
    if (this.destroyed) return;

    if (this.timer) clearTimeout(this.timer);

    const after = this.highestId();

    app
      .request({
        method: 'GET',
        url: `${app.forum.attribute('apiUrl')}/happy-quiz/contests/${this.contestId()}/chat`,
        params: after ? { after } : {},
      })
      .then((response) => {
        if (this.destroyed) return;

        const data = response.data || {};

        this.open = !!data.open;
        this.canPost = !!data.canPost;
        this.loading = false;
        this.failures = 0;

        this.merge(data.messages || []);
        this.schedule();
        m.redraw();
      })
      .catch(() => {
        if (this.destroyed) return;

        this.loading = false;
        this.failures++;
        this.schedule();
        m.redraw();
      });
  }

  /**
   * Adds messages we do not already hold, and drops any optimistic copy the
   * server has now confirmed.
   */
  merge(incoming) {
    if (!incoming.length) return;

    const known = new Set(this.messages.filter((m) => m.id).map((m) => m.id));

    const fresh = incoming.filter((message) => !known.has(message.id));

    if (!fresh.length) return;

    // An optimistic message is matched by its own text and author, so the
    // confirmed row replaces it instead of appearing twice.
    const pendingLeft = this.messages.filter((message) => {
      if (!message.pending) return true;

      return !fresh.some((f) => f.isMe && f.content === message.content);
    });

    this.messages = [...pendingLeft, ...fresh].sort((a, b) => (a.id || Infinity) - (b.id || Infinity));
  }

  schedule() {
    if (this.destroyed) return;

    let delay = document.hidden ? POLL_HIDDEN : POLL_ACTIVE;

    // Back off after failures rather than retrying into an outage.
    if (this.failures > 0) {
      delay = Math.min(60000, delay * Math.pow(2, Math.min(this.failures, 4)));
    }

    this.timer = setTimeout(() => this.poll(), delay);
  }

  send() {
    const content = this.draft.trim();

    if (!content || this.sending) return;

    this.sending = true;
    this.error = null;

    // Shown straight away, with a marker so it can be reconciled or rolled
    // back when the server answers.
    const optimistic = {
      id: null,
      pending: true,
      content,
      createdAt: new Date().toISOString(),
      userId: app.session.user?.id(),
      username: app.session.user?.displayName(),
      avatarUrl: app.session.user?.avatarUrl(),
      isMe: true,
    };

    this.messages = [...this.messages, optimistic];
    this.draft = '';

    // Sending is an explicit act: follow it down even if the reader had
    // scrolled up.
    this.pinToEnd = true;

    app
      .request({
        method: 'POST',
        url: `${app.forum.attribute('apiUrl')}/happy-quiz/contests/${this.contestId()}/chat`,
        body: { data: { content } },
      })
      .then((response) => {
        if (this.destroyed) return;

        this.sending = false;
        this.merge(response.data?.messages || []);
        m.redraw();
      })
      .catch((error) => {
        if (this.destroyed) return;

        this.sending = false;

        // Roll the optimistic entry back and hand the text back to the user
        // rather than losing what they typed.
        this.messages = this.messages.filter((message) => message !== optimistic);
        this.draft = content;

        const code = error?.response?.errors?.[0]?.code;

        this.error = app.translator.trans(
          `happydigital-quiz.forum.chat.error_${code || 'generic'}`
        );

        m.redraw();
      });
  }

  /**
   * Whether the reader is close enough to the bottom that new messages should
   * scroll into view.
   */
  atEnd() {
    if (!this.list) return true;

    const distance = this.list.scrollHeight - this.list.scrollTop - this.list.clientHeight;

    return distance < STICK_THRESHOLD;
  }

  scrollToEnd() {
    if (this.list) this.list.scrollTop = this.list.scrollHeight;
  }

  view() {
    const t = (key, params) => app.translator.trans(`happydigital-quiz.forum.chat.${key}`, params);

    return (
      <div className="HappyQuizChat">
        <div className="HappyQuizChat-head">
          <h2>{t('heading')}</h2>
          <span className="HappyQuiz-small">{t('subtitle')}</span>
        </div>

        <div className="HappyQuizChat-list">
          {this.loading ? (
            <div className="HappyQuizChat-state">
              <LoadingIndicator />
            </div>
          ) : this.messages.length ? (
            this.messages.map((message, i) => this.bubble(message, this.messages[i - 1]))
          ) : (
            <div className="HappyQuizChat-state">{t('empty')}</div>
          )}
        </div>

        {this.composer(t)}
      </div>
    );
  }

  /**
   * One message. Consecutive messages from the same person drop the repeated
   * avatar and name, the way a chat app groups them.
   */
  bubble(message, previous) {
    const grouped = previous && previous.userId === message.userId && previous.isMe === message.isMe;

    const classes = ['HappyQuizChat-message'];

    if (message.isMe) classes.push('is-me');
    if (grouped) classes.push('is-grouped');
    if (message.pending) classes.push('is-pending');

    return (
      <div className={classes.join(' ')}>
        {/* Shown for everyone including the viewer; a grouped run keeps the
            spacer so the bubbles under it stay in line. */}
        {!grouped ? (
          <span className="HappyQuizChat-avatar">
            {message.avatarUrl ? (
              <img src={message.avatarUrl} alt="" />
            ) : (
              (message.username || '?').charAt(0).toLocaleUpperCase()
            )}
          </span>
        ) : (
          <span className="HappyQuizChat-avatarSpacer" />
        )}

        <div className="HappyQuizChat-body">
          {/* The name is only worth stating for other people. */}
          {!message.isMe && !grouped ? <div className="HappyQuizChat-author">{message.username}</div> : null}

          <div className="HappyQuizChat-bubble">
            {/* Plain text: user input is never trusted as markup. */}
            <span className="HappyQuizChat-text">{message.content}</span>
          </div>

          {/* Outside the bubble, so a short message stays a short, narrow
              box instead of stretching to fit the timestamp beside it. */}
          <span className="HappyQuizChat-time">{this.time(message.createdAt)}</span>
        </div>
      </div>
    );
  }

  time(iso) {
    if (!iso) return '';

    const date = new Date(iso);

    if (Number.isNaN(date.getTime())) return '';

    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }

  composer(t) {
    if (!this.open) {
      return <div className="HappyQuizChat-closed">{t('closed')}</div>;
    }

    if (!app.session.user) {
      return <div className="HappyQuizChat-closed">{t('login_required')}</div>;
    }

    if (!this.canPost) {
      return <div className="HappyQuizChat-closed">{t('join_required')}</div>;
    }

    const remaining = MAX_LENGTH - this.draft.length;

    return (
      <div className="HappyQuizChat-composer">
        {this.error ? <div className="HappyQuizChat-error">{this.error}</div> : null}

        <div className="HappyQuizChat-inputRow">
          <textarea
            className="HappyQuizChat-input"
            rows="1"
            maxlength={MAX_LENGTH}
            placeholder={t('placeholder')}
            value={this.draft}
            oninput={(e) => {
              this.draft = e.target.value;
              this.autoGrow(e.target);
            }}
            onkeydown={(e) => {
              // Enter sends; Shift+Enter starts a new line.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.send();
              }
            }}
          />

          <button
            type="button"
            className="HappyQuizChat-send"
            disabled={!this.draft.trim() || this.sending}
            aria-label={t('send')}
            onclick={() => this.send()}
          >
            <i className="fas fa-paper-plane" aria-hidden="true" />
          </button>
        </div>

        {remaining < 80 ? <div className="HappyQuizChat-count">{remaining}</div> : null}
      </div>
    );
  }

  /**
   * Grows the box with its content up to a few lines, then scrolls.
   */
  autoGrow(el) {
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 96)}px`;
  }
}
